import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import GestorLayout from '../../components/GestorLayout';
import GestaoLotes from '../../components/GestaoLotes';
import AcompanhamentoFullscreen from '../../components/AcompanhamentoFullscreen';
import AnaliseTemposOF from '../../components/AnaliseTemposOF';
import EditarRegisto from '../../components/gestor/EditarRegisto';
import { 
  OFCard, 
  OFFilters, 
  OFStatePanel, 
  OFListRow, 
  OFStatusDialog,
  OFProgressDialog,
  OFEmptyState,
  OperacoesSelector,
  ModalCriarResgate,
  ModalRegistosProducao,
  ModalCriarMinuta,
  ModalNovaOF,
  ModalEditarOF,
} from '../../components/gestor/ofs';
import { OperadorTags } from '../../components/GestaoTags';
import { useAuth } from '../../context/AuthContext';
import { OFProvider, useOF } from '../../context/OFContext';
import axios from 'axios';
import { toast } from 'sonner';
import { getErrorMessage, formatTimeLisbon, formatOnlyDateLisbon } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { 
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, 
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle 
} from '../../components/ui/alert-dialog';
import { Input } from '../../components/ui/input';
import { 
  Plus, Search, ChevronRight, ChevronLeft, ChevronDown, ChevronUp, Save, Users, Trash2, 
  FileText, QrCode, Layers, Grid, List, Edit, Play, Pause, Package,
  CheckCircle, XCircle, Eye, Clock, ArrowRight, BarChart3, GripVertical,
  AlertTriangle, X, Zap, Box, Wrench, Tag, Filter, Activity, Check, Sparkles, PieChart,
  ArrowUp, ArrowDown, Edit3, Download, Upload, CheckSquare, Square
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_CONFIG = {
  rascunho: { label: 'Rascunho', color: 'bg-stone-100 text-stone-600', icon: FileText },
  pendente: { label: 'Ready to Start', color: 'bg-blue-100 text-blue-600', icon: CheckCircle },
  aguardar_entrada: { label: 'Aguardar Entrada', color: 'bg-amber-100 text-amber-600', icon: Clock },
  em_curso: { label: 'Em Curso', color: 'bg-green-100 text-green-600', icon: Play },
  pausado: { label: 'Pausado', color: 'bg-orange-100 text-orange-600', icon: Pause },
  terminado: { label: 'Terminado', color: 'bg-emerald-100 text-emerald-600', icon: CheckCircle },
  cancelado: { label: 'Cancelado', color: 'bg-red-100 text-red-600', icon: XCircle },
};

const TRANSICOES_VALIDAS = {
  rascunho: ['pendente', 'aguardar_entrada', 'em_curso', 'cancelado'],
  pendente: ['aguardar_entrada', 'em_curso', 'cancelado'],
  aguardar_entrada: ['pendente', 'em_curso', 'cancelado'],
  em_curso: ['pausado', 'terminado', 'cancelado'],
  pausado: ['em_curso', 'terminado', 'cancelado'],
  terminado: ['em_curso', 'pendente', 'rascunho'],
  cancelado: ['rascunho', 'pendente'],
};

function GestaoOFsContent() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Estados do contexto (dados partilhados)
  const { 
    ofs, 
    operacoes, 
    operadores, 
    modelos, 
    clientes: clientesDB,
    tags: allTags,
    categorias: allCategorias,
    categoriasEspeciais,
    permissoesOperadores,
    loading: contextLoading,
    initialLoadDone,
    loadOfs,
    loadOperacoes,
    loadOperadores,
    loadModelos,
    operadorTemPermissao,
    filtrarOperadoresPorPermissao,
    existeOperadorComPermissao,
    setOfs
  } = useOF();
  
  // Estados locais específicos da UI (não duplicados)
  const [showDialog, setShowDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editOf, setEditOf] = useState(null);
  const [editInitialStep, setEditInitialStep] = useState(1);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [showProgressDialog, setShowProgressDialog] = useState(false);
  const [showPainelEstados, setShowPainelEstados] = useState(false);
  const [showLotesDialog, setShowLotesDialog] = useState(false);
  const [showAcompanharFullscreen, setShowAcompanharFullscreen] = useState(false);
  const [showAnaliseTempos, setShowAnaliseTempos] = useState(false);
  const [selectedOfForAcompanhar, setSelectedOfForAcompanhar] = useState(null);
  const [selectedOfForLotes, setSelectedOfForLotes] = useState(null);
  const [selectedOfForAnalise, setSelectedOfForAnalise] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showCriarMinutaModal, setShowCriarMinutaModal] = useState(false);
  const [minutaInitialData, setMinutaInitialData] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCliente, setFilterCliente] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [selectedOF, setSelectedOF] = useState(null);
  const [ofsPorEstado, setOfsPorEstado] = useState({});
  const [progressoDetalhado, setProgressoDetalhado] = useState(null);
  const [participantes, setParticipantes] = useState([]);
  const [anomaliasPorOf, setAnomaliasPorOf] = useState({});
  const [sortBy, setSortBy] = useState('data_criacao');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedOfsForExport, setSelectedOfsForExport] = useState(new Set());
  const [selectionMode, setSelectionMode] = useState(false);
  
  // Estados para Paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  
  // Estados para Anomalias de Produção e Resgates
  const [anomaliasProducao, setAnomaliasProducao] = useState([]);
  const [anomaliasSelecionadas, setAnomaliasSelecionadas] = useState({});
  
  // Estados para Edição de Registos
  const [showEditarRegisto, setShowEditarRegisto] = useState(false);
  const [selectedRegistoId, setSelectedRegistoId] = useState(null);
  const [showRegistosDialog, setShowRegistosDialog] = useState(false);
  const [selectedOpForRegistos, setSelectedOpForRegistos] = useState(null);
  const [registosOperacao, setRegistosOperacao] = useState([]);
  const [showResgateModal, setShowResgateModal] = useState(false);
  const [resgates, setResgates] = useState([]);
  
  const [statusObservacao, setStatusObservacao] = useState('');

  // Formatar nome do operador (primeiro e último nome)
  const formatarNomeOperador = (nomeCompleto) => {
    if (!nomeCompleto) return '?';
    const partes = nomeCompleto.trim().split(' ');
    if (partes.length === 1) return partes[0];
    return `${partes[0]} ${partes[partes.length - 1]}`;
  };

  // Função para abrir gestão de lotes
  const handleOpenLotes = (of) => {
    setSelectedOfForLotes(of);
    setShowLotesDialog(true);
  };
  
  // Toggle para expandir/colapsar uma operação
  const toggleExpandOp = (opId) => {
    setExpandedOps(prev => 
      prev.includes(opId) 
        ? prev.filter(id => id !== opId)
        : [...prev, opId]
    );
  };

  // NOTE: Drag & Drop and operation management functions moved to ModalEditarOF.js

  // Carregar dados específicos que não estão no contexto (anomalias, ofsPorEstado)
  useEffect(() => {
    loadOfsPorEstado();
    loadAnomalias();
  }, []);

  // Verificar se há parâmetros 'of' e 'tab' na URL para abrir edição/acompanhar diretamente
  useEffect(() => {
    const ofIdFromUrl = searchParams.get('of');
    const tabFromUrl = searchParams.get('tab');
    const criarFromUrl = searchParams.get('criar');
    const loteIdFromUrl = searchParams.get('lote');
    
    // Abrir modal de criar OF se vier do Planeamento
    if (criarFromUrl === 'true') {
      setShowDialog(true);
      setSearchParams({});
      return;
    }
    
    if (ofIdFromUrl && ofs.length > 0) {
      const ofToOpen = ofs.find(of => of.id === ofIdFromUrl);
      if (ofToOpen) {
        if (tabFromUrl === 'acompanhar') {
          // Fechar gestão de lotes se estiver aberta (para transição suave)
          setShowLotesDialog(false);
          setSelectedOfForLotes(null);
          // Abrir painel de acompanhamento fullscreen
          setSelectedOfForAcompanhar({ ...ofToOpen, loteIdPreSelecionado: loteIdFromUrl });
          setShowAcompanharFullscreen(true);
        } else if (tabFromUrl === 'lotes') {
          // Abrir diálogo de gestão de lotes
          setSelectedOfForLotes(ofToOpen);
          setShowLotesDialog(true);
        } else if (tabFromUrl === 'operacoes') {
          // Abrir diálogo de edição na tab de operações
          openEditDialogOperacoes(ofToOpen);
        } else if (tabFromUrl === 'editar') {
          // Abrir diálogo de edição
          openEditDialog(ofToOpen);
        } else {
          // Abrir diálogo de edição normalmente
          openEditDialog(ofToOpen);
        }
        // Limpar os parâmetros da URL depois de abrir
        setSearchParams({});
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ofs, searchParams]);

  const loadAnomalias = async () => {
    try {
      const response = await axios.get(`${API}/ocorrencias`);
      // Agrupar anomalias por OF
      const porOf = {};
      response.data.forEach(oc => {
        if (!porOf[oc.of_id]) {
          porOf[oc.of_id] = { total: 0, abertas: 0 };
        }
        porOf[oc.of_id].total++;
        if (oc.status === 'aberta' || oc.status === 'em_analise') {
          porOf[oc.of_id].abertas++;
        }
      });
      setAnomaliasPorOf(porOf);
    } catch (error) {
      console.error('Erro ao carregar anomalias:', error);
    }
  };

  const loadOfsPorEstado = async () => {
    try {
      const response = await axios.get(`${API}/ofs/por-estado/resumo`);
      setOfsPorEstado(response.data);
    } catch (error) {
      console.error('Erro ao carregar OFs por estado:', error);
    }
  };

  const loadProgressoDetalhado = async (ofId) => {
    try {
      const [progressoRes, participantesRes] = await Promise.all([
        axios.get(`${API}/ofs/${ofId}/progresso-detalhado`),
        axios.get(`${API}/ofs/${ofId}/participantes`)
      ]);
      setProgressoDetalhado(progressoRes.data);
      setParticipantes(participantesRes.data);
    } catch (error) {
      console.error('Erro ao carregar progresso:', error);
      toast.error('Erro ao carregar progresso da OF');
    }
  };

  // Carregar registos de uma operação específica
  const loadRegistosOperacao = async (ofId, operacaoId) => {
    try {
      const res = await axios.get(`${API}/ofs/${ofId}/registos`);
      // Filtrar apenas registos desta operação
      const registosFiltrados = res.data.filter(r => r.operacao_id === operacaoId);
      setRegistosOperacao(registosFiltrados);
    } catch (error) {
      console.error('Erro ao carregar registos:', error);
      toast.error('Erro ao carregar registos');
    }
  };

  // Efeito para carregar registos quando abre o diálogo
  useEffect(() => {
    if (showRegistosDialog && selectedOpForRegistos && progressoDetalhado?.of?.id) {
      loadRegistosOperacao(progressoDetalhado.of.id, selectedOpForRegistos.operacao_id);
    }
  }, [showRegistosDialog, selectedOpForRegistos, progressoDetalhado?.of?.id]);

  // NOTE: Operation management functions (handleToggleOperacao, handleRemoveOperacao,
  // handleAjustarTempo, handleSetGrupo, handleRemoverGrupagem, handleRemoverOperacao)
  // moved to ModalEditarOF.js with their corresponding state (selectedOperacoes)

  const [deleteConfirmOF, setDeleteConfirmOF] = useState(null);

  const handleDeleteOF = async (of) => {
    setDeleteConfirmOF(of);
  };

  const confirmDeleteOF = async () => {
    if (!deleteConfirmOF) return;
    const of = deleteConfirmOF;
    setDeleteConfirmOF(null);
    setLoading(true);
    try {
      await axios.delete(`${API}/ofs/${of.id}`);
      toast.success(`OF ${of.numero_of} eliminada com sucesso`);
      loadOfs();
      loadOfsPorEstado();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao eliminar OF'));
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleTransitarStatus = async (novoStatus) => {
    if (!selectedOF) return;
    
    setLoading(true);
    try {
      await axios.patch(`${API}/ofs/${selectedOF.id}/status`, {
        status: novoStatus,
        observacao: statusObservacao || undefined
      });

      toast.success(`OF transitada para ${STATUS_CONFIG[novoStatus]?.label || novoStatus}`);
      setShowStatusDialog(false);
      setStatusObservacao('');
      setSelectedOF(null);
      loadOfs();
      loadOfsPorEstado();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao transitar OF'));
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = async (of) => {
    await loadOperacoes();
    setSelectedOF(of);
    setEditOf(of);
    setEditInitialStep(1);
    setShowEditDialog(true);
  };

  // Abrir diálogo de edição diretamente na aba de Operações
  const openEditDialogOperacoes = async (of) => {
    await loadOperacoes();
    setSelectedOF(of);
    setEditOf(of);
    setEditInitialStep(3);
    setShowEditDialog(true);
  };

  const openStatusDialog = (of) => {
    setSelectedOF(of);
    setShowStatusDialog(true);
  };

  const openProgressDialog = async (of) => {
    setSelectedOF(of);
    await loadProgressoDetalhado(of.id);
    setShowProgressDialog(true);
  };

  // Função para abrir o painel de Acompanhamento da OF (fullscreen)
  const handleOpenAcompanhar = (of) => {
    setSelectedOfForAcompanhar(of);
    setShowAcompanharFullscreen(true);
  };

  const handleOpenAnaliseTempos = (of) => {
    setSelectedOfForAnalise(of);
    setShowAnaliseTempos(true);
  };

  // Função para criar resgate de peças
  const handleCriarResgate = async () => {
    const anomaliasParaResgate = Object.entries(anomaliasSelecionadas)
      .filter(([_, qtd]) => qtd > 0)
      .map(([anomalia_id, quantidade]) => ({ anomalia_id, quantidade: parseInt(quantidade) }));
    
    if (anomaliasParaResgate.length === 0) {
      toast.error('Selecione pelo menos uma anomalia para resgatar');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/resgates?gestor_id=gestor`, {
        anomalias: anomaliasParaResgate,
        observacoes: 'Resgate criado pelo gestor'
      });
      
      toast.success(`Resgate ${response.data.numero_resgate} criado com sucesso!`);
      
      // Gerar PDF do resgate (separado para não bloquear em caso de erro)
      try {
        const pdfResponse = await axios.get(`${API}/resgates/${response.data.id}/pdf`);
        if (pdfResponse.data.pdf_url) {
          window.open(`${BACKEND_URL}${pdfResponse.data.pdf_url}`, '_blank');
        }
      } catch (pdfError) {
        console.warn('Não foi possível gerar PDF do resgate:', pdfError);
      }
      
      // Recarregar dados
      const [anomaliasRes, resgatesRes] = await Promise.all([
        axios.get(`${API}/ofs/${selectedOF.id}/anomalias-producao`),
        axios.get(`${API}/resgates?of_id=${selectedOF.id}`)
      ]);
      setAnomaliasProducao(anomaliasRes.data);
      setResgates(resgatesRes.data);
      setAnomaliasSelecionadas({});
      setShowResgateModal(false);
    } catch (error) {
      console.error('Erro ao criar resgate:', error);
      toast.error(getErrorMessage(error, 'Erro ao criar resgate'));
    } finally {
      setLoading(false);
    }
  };

  // Calcular quantidade disponível para resgate
  const getQuantidadeDisponivelResgate = (anomalia) => {
    return (anomalia.quantidade || 0) - 
           (anomalia.quantidade_em_arranjo || 0) - 
           (anomalia.quantidade_recuperada || 0) - 
           (anomalia.quantidade_descartada || 0);
  };

  const resetForm = () => {
    setSelectedOF(null);
    setEditOf(null);
  };

  // Callback chamado pelo ModalEditarOF quando o utilizador clica em "Criar Minuta"
  const handleCriarMinutaCallback = (ops) => {
    if (!selectedOF) return;
    setMinutaInitialData({
      nome: `Minuta - ${selectedOF.numero_of} - ${selectedOF.artigo}`,
      descricao: `Baseada na OF ${selectedOF.numero_of}`,
      operacoes: ops,
    });
    setShowCriarMinutaModal(true);
  };

  // Callback para abrir resgate a partir do ModalEditarOF
  const handleOpenResgateFromEdit = async (ofId) => {
    const of = ofs.find((o) => o.id === ofId) || selectedOF;
    if (!of) return;
    setSelectedOF(of);
    setLoading(true);
    try {
      const [anomRes, resgRes] = await Promise.all([
        axios.get(`${API}/ofs/${ofId}/anomalias-producao`),
        axios.get(`${API}/resgates?of_id=${ofId}`),
      ]);
      setAnomaliasProducao(anomRes.data);
      setResgates(resgRes.data);
      setAnomaliasSelecionadas({});
      setShowResgateModal(true);
    } catch (e) {
      toast.error('Erro ao carregar anomalias');
    } finally {
      setLoading(false);
    }
  };

  const formatTempo = (segundos) => {
    if (!segundos || segundos === 0) return '0s';
    if (segundos >= 60) {
      const min = Math.floor(segundos / 60);
      const sec = segundos % 60;
      return sec > 0 ? `${min}min ${sec}s` : `${min}min`;
    }
    return `${segundos}s`;
  };

  // NOTE: getTotalLotes removed - lotes state moved to ModalEditarOF.js

  // Filtragem e Ordenação (sobre toda a lista)
  const filteredOfs = ofs
    .filter(of => {
      const matchesSearch = of.numero_of.toLowerCase().includes(searchTerm.toLowerCase()) ||
        of.artigo.toLowerCase().includes(searchTerm.toLowerCase()) ||
        of.cliente.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = !filterStatus || of.status === filterStatus;
      const matchesCliente = !filterCliente || of.cliente === filterCliente;
      return matchesSearch && matchesStatus && matchesCliente;
    })
    .sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case 'data_entrega':
          comparison = (a.data_entrega || '').localeCompare(b.data_entrega || '');
          break;
        case 'previsao_entrada':
          comparison = (a.previsao_entrada || '').localeCompare(b.previsao_entrada || '');
          break;
        case 'cliente':
          comparison = (a.cliente || '').localeCompare(b.cliente || '');
          break;
        case 'status':
          const statusOrder = ['em_curso', 'pausado', 'pendente', 'aguardar_entrada', 'rascunho', 'terminado', 'cancelado'];
          comparison = statusOrder.indexOf(a.status) - statusOrder.indexOf(b.status);
          break;
        case 'progresso':
          const progressoA = a.progresso_real || 0;
          const progressoB = b.progresso_real || 0;
          comparison = progressoA - progressoB;
          break;
        case 'numero_of':
          comparison = (a.numero_of || '').localeCompare(b.numero_of || '');
          break;
        case 'quantidade':
          comparison = (a.quantidade_total || 0) - (b.quantidade_total || 0);
          break;
        case 'lotes':
          comparison = (a.lotes?.length || 0) - (b.lotes?.length || 0);
          break;
        default: // data_criacao
          comparison = (a.criado_em || '').localeCompare(b.criado_em || '');
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });

  // Paginação - aplicada APÓS filtros e ordenação
  const totalPages = Math.ceil(filteredOfs.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedOfs = filteredOfs.slice(startIndex, startIndex + itemsPerPage);
  
  // Reset página ao mudar filtros
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterStatus, filterCliente, sortBy, sortOrder]);

  // Lista única de clientes para o filtro
  const clientesUnicos = [...new Set(ofs.map(of => of.cliente))].sort();

  const getStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.pendente;
    const Icon = config.icon;
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${config.color}`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </span>
    );
  };

  const downloadPdfOF = async (ofId, numeroOf) => {
    try {
      const response = await axios.get(`${API}/ofs/${ofId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `OF_${numeroOf}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF da OF gerado com sucesso!');
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      toast.error('Erro ao gerar PDF da OF');
    }
  };

  const exportCsvOF = async (ofId, numeroOf) => {
    try {
      const response = await axios.get(`${API}/ofs/${ofId}/export-csv`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `OF_${numeroOf}_export.csv`;
      link.style.display = 'none';
      document.body.appendChild(link);
      setTimeout(() => {
        link.click();
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }, 500);
      }, 100);
      toast.success(`CSV da OF ${numeroOf} exportado!`);
    } catch (error) {
      console.error('Erro ao exportar CSV:', error);
      toast.error('Erro ao exportar CSV');
    }
  };

  const importCsvOF = async (file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      setLoading(true);
      const res = await axios.post(`${API}/ofs/import-csv`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(res.data?.message || 'OF(s) importada(s) com sucesso!');
      loadOfs();
      loadOfsPorEstado();
    } catch (error) {
      console.error('Erro ao importar CSV:', error);
      toast.error(getErrorMessage(error, 'Erro ao importar CSV'));
    } finally {
      setLoading(false);
    }
  };

  const exportBulkCsv = async () => {
    if (selectedOfsForExport.size === 0) {
      toast.error('Selecione pelo menos uma OF para exportar');
      return;
    }
    try {
      const response = await axios.post(
        `${API}/ofs/export-csv-bulk`,
        { of_ids: Array.from(selectedOfsForExport) },
        { responseType: 'blob' }
      );
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `OFs_export_${selectedOfsForExport.size}.csv`;
      link.style.display = 'none';
      document.body.appendChild(link);
      setTimeout(() => {
        link.click();
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }, 500);
      }, 100);
      toast.success(`${selectedOfsForExport.size} OF(s) exportada(s) com sucesso!`);
      setSelectedOfsForExport(new Set());
      setSelectionMode(false);
    } catch (error) {
      console.error('Erro ao exportar CSV em massa:', error);
      toast.error('Erro ao exportar CSV');
    }
  };

  const toggleOfSelection = (ofId) => {
    setSelectedOfsForExport(prev => {
      const next = new Set(prev);
      if (next.has(ofId)) next.delete(ofId);
      else next.add(ofId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedOfsForExport.size === paginatedOfs.length) {
      setSelectedOfsForExport(new Set());
    } else {
      setSelectedOfsForExport(new Set(paginatedOfs.map(of => of.id)));
    }
  };

  const getGrupoCor = (grupo) => {
    const cores = {
      'A': 'border-l-blue-500 bg-blue-50',
      'B': 'border-l-green-500 bg-green-50',
      'C': 'border-l-amber-500 bg-amber-50',
      'D': 'border-l-purple-500 bg-purple-50',
      'E': 'border-l-pink-500 bg-pink-50'
    };
    return cores[grupo] || 'border-l-transparent';
  };

  // Cores para categorias de operações
  const getCategoriaCor = (categoria) => {
    const cores = {
      'preparacao': { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-300' },
      'preparação': { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-300' },
      'acabamentos': { bg: 'bg-emerald-100', text: 'text-emerald-800', border: 'border-emerald-300' },
      'embalamento': { bg: 'bg-sky-100', text: 'text-sky-800', border: 'border-sky-300' },
      'intermedios': { bg: 'bg-violet-100', text: 'text-violet-800', border: 'border-violet-300' },
      'intermédios': { bg: 'bg-violet-100', text: 'text-violet-800', border: 'border-violet-300' },
      'costura': { bg: 'bg-rose-100', text: 'text-rose-800', border: 'border-rose-300' },
      'corte': { bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-300' },
      'lavagem': { bg: 'bg-cyan-100', text: 'text-cyan-800', border: 'border-cyan-300' },
      'passagem': { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-300' },
      'controlo': { bg: 'bg-indigo-100', text: 'text-indigo-800', border: 'border-indigo-300' },
    };
    const key = categoria?.toLowerCase();
    return cores[key] || { bg: 'bg-stone-100', text: 'text-stone-700', border: 'border-stone-300' };
  };

  return (
    <GestorLayout>
      <div className="px-8 pt-4 pb-8" data-testid="gestao-ofs-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-heading font-bold text-stone-900 mb-2">Ordens de Fabrico</h1>
            <p className="text-stone-600">Gerir ordens de fabrico ({filteredOfs.length} total)</p>
          </div>
          <div className="flex items-center gap-3">
            {/* Import/Export discretos — primeiro na linha */}
            <label className="cursor-pointer">
              <input
                type="file"
                accept=".csv"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) importCsvOF(file);
                  e.target.value = '';
                }}
                data-testid="import-csv-input"
              />
              <span className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-stone-500 hover:text-stone-700 hover:bg-stone-100 rounded-lg cursor-pointer transition-colors">
                <Upload className="h-4 w-4" />
                Importar
              </span>
            </label>
            {!selectionMode ? (
              <button
                onClick={() => { setSelectionMode(true); setSelectedOfsForExport(new Set()); }}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-stone-500 hover:text-stone-700 hover:bg-stone-100 rounded-lg transition-colors"
                data-testid="btn-modo-exportar"
              >
                <Download className="h-4 w-4" />
                Exportar
              </button>
            ) : (
              <div className="flex items-center gap-2 bg-blue-50 px-3 py-1.5 rounded-full border border-blue-200">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={toggleSelectAll}
                  className="text-blue-700 hover:bg-blue-100 px-2"
                  data-testid="btn-selecionar-todas"
                >
                  {selectedOfsForExport.size === paginatedOfs.length ? <CheckSquare className="h-4 w-4 mr-1" /> : <Square className="h-4 w-4 mr-1" />}
                  {selectedOfsForExport.size === paginatedOfs.length ? 'Desmarcar' : 'Todas'}
                </Button>
                <span className="text-sm font-medium text-blue-700">{selectedOfsForExport.size} selecionada(s)</span>
                <Button
                  size="sm"
                  onClick={exportBulkCsv}
                  disabled={selectedOfsForExport.size === 0}
                  data-testid="btn-exportar-selecionadas"
                  className="rounded-full"
                >
                  <Download className="h-4 w-4 mr-1" />
                  Exportar CSV
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => { setSelectionMode(false); setSelectedOfsForExport(new Set()); }}
                  className="text-stone-500 hover:text-stone-700 px-1"
                  data-testid="btn-cancelar-exportar"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )}

            <div className="w-px h-6 bg-stone-200" />

            <Button
              variant="outline"
              onClick={() => setShowPainelEstados(!showPainelEstados)}
              className="rounded-full"
              data-testid="btn-painel-estados"
            >
              <BarChart3 className="h-4 w-4 mr-2" />
              Painel de Estados
            </Button>
            <div className="flex border rounded-full overflow-hidden">
              <Button
                variant={viewMode === 'grid' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('grid')}
                className="rounded-none"
              >
                <Grid className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
                className="rounded-none"
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
            {/* Nova OF — botão maior e destacado */}
            <Button
              onClick={() => setShowDialog(true)}
              className="rounded-full px-6 py-3 text-base font-bold"
              data-testid="criar-of-button"
            >
              <Plus className="h-5 w-5 mr-2" />
              Nova OF
            </Button>
          </div>
        </div>

        {/* Painel de Estados */}
        {showPainelEstados && (
          <OFStatePanel
            ofsPorEstado={ofsPorEstado}
            filterStatus={filterStatus}
            setFilterStatus={setFilterStatus}
          />
        )}

        {/* Filtros */}
        <OFFilters
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          filterCliente={filterCliente}
          setFilterCliente={setFilterCliente}
          filterStatus={filterStatus}
          setFilterStatus={setFilterStatus}
          sortBy={sortBy}
          setSortBy={setSortBy}
          sortOrder={sortOrder}
          setSortOrder={setSortOrder}
          clientesUnicos={clientesUnicos}
        />

        {/* Vista Grid */}
        {viewMode === 'grid' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {paginatedOfs.map((of) => (
              <OFCard
                key={of.id}
                of={of}
                anomalias={anomaliasPorOf[of.id]}
                categorias={allCategorias}
                onOpenLotes={handleOpenLotes}
                onOpenAcompanhar={handleOpenAcompanhar}
                onOpenAnaliseTempos={handleOpenAnaliseTempos}
                onOpenEdit={openEditDialog}
                onOpenOperacoes={openEditDialogOperacoes}
                onOpenStatus={openStatusDialog}
                onDelete={handleDeleteOF}
                onExportCsv={exportCsvOF}
                getStatusBadge={getStatusBadge}
                selectionMode={selectionMode}
                isSelected={selectedOfsForExport.has(of.id)}
                onToggleSelect={toggleOfSelection}
              />
            ))}
          </div>
        )}

        {/* Vista Lista */}
        {viewMode === 'list' && (
          <Card className="overflow-hidden">
            <table className="w-full">
              <thead className="bg-stone-50">
                <tr>
                  {selectionMode && (
                    <th className="w-10 py-3 px-2">
                      <button onClick={toggleSelectAll} className="flex items-center justify-center">
                        {selectedOfsForExport.size === paginatedOfs.length
                          ? <CheckSquare className="h-5 w-5 text-blue-600" />
                          : <Square className="h-5 w-5 text-stone-400 hover:text-blue-500" />}
                      </button>
                    </th>
                  )}
                  <th 
                    className="text-left py-3 px-4 text-sm font-semibold text-stone-600 cursor-pointer hover:bg-stone-100 transition-colors"
                    onClick={() => { setSortBy('numero_of'); setSortOrder(sortBy === 'numero_of' && sortOrder === 'asc' ? 'desc' : 'asc'); }}
                  >
                    <div className="flex items-center gap-1">
                      OF {sortBy === 'numero_of' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </div>
                  </th>
                  <th 
                    className="text-left py-3 px-4 text-sm font-semibold text-stone-600 cursor-pointer hover:bg-stone-100 transition-colors"
                    onClick={() => { setSortBy('cliente'); setSortOrder(sortBy === 'cliente' && sortOrder === 'asc' ? 'desc' : 'asc'); }}
                  >
                    <div className="flex items-center gap-1">
                      Marca {sortBy === 'cliente' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </div>
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-stone-600">Modelo</th>
                  <th 
                    className="text-center py-3 px-4 text-sm font-semibold text-stone-600 cursor-pointer hover:bg-stone-100 transition-colors"
                    onClick={() => { setSortBy('quantidade'); setSortOrder(sortBy === 'quantidade' && sortOrder === 'asc' ? 'desc' : 'asc'); }}
                  >
                    <div className="flex items-center justify-center gap-1">
                      Qtd. {sortBy === 'quantidade' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </div>
                  </th>
                  <th 
                    className="text-center py-3 px-4 text-sm font-semibold text-stone-600 cursor-pointer hover:bg-stone-100 transition-colors"
                    onClick={() => { setSortBy('lotes'); setSortOrder(sortBy === 'lotes' && sortOrder === 'asc' ? 'desc' : 'asc'); }}
                  >
                    <div className="flex items-center justify-center gap-1">
                      Lotes {sortBy === 'lotes' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </div>
                  </th>
                  <th 
                    className="text-center py-3 px-4 text-sm font-semibold text-stone-600 cursor-pointer hover:bg-stone-100 transition-colors"
                    onClick={() => { setSortBy('progresso'); setSortOrder(sortBy === 'progresso' && sortOrder === 'asc' ? 'desc' : 'asc'); }}
                  >
                    <div className="flex items-center justify-center gap-1">
                      Progresso {sortBy === 'progresso' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </div>
                  </th>
                  <th 
                    className="text-center py-3 px-4 text-sm font-semibold text-stone-600 cursor-pointer hover:bg-stone-100 transition-colors"
                    onClick={() => { setSortBy('data_entrega'); setSortOrder(sortBy === 'data_entrega' && sortOrder === 'asc' ? 'desc' : 'asc'); }}
                  >
                    <div className="flex items-center justify-center gap-1">
                      Entrega {sortBy === 'data_entrega' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </div>
                  </th>
                  <th 
                    className="text-center py-3 px-4 text-sm font-semibold text-stone-600 cursor-pointer hover:bg-stone-100 transition-colors"
                    onClick={() => { setSortBy('status'); setSortOrder(sortBy === 'status' && sortOrder === 'asc' ? 'desc' : 'asc'); }}
                  >
                    <div className="flex items-center justify-center gap-1">
                      Estado {sortBy === 'status' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </div>
                  </th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-stone-600">Ações</th>
                </tr>
              </thead>
              <tbody>
                {paginatedOfs.map((of) => (
                  <OFListRow
                    key={of.id}
                    of={of}
                    anomalias={anomaliasPorOf[of.id]}
                    onOpenAcompanhar={handleOpenAcompanhar}
                    onOpenAnaliseTempos={handleOpenAnaliseTempos}
                    onOpenLotes={handleOpenLotes}
                    onOpenEdit={openEditDialog}
                    onOpenStatus={openStatusDialog}
                    onDelete={handleDeleteOF}
                    onExportCsv={exportCsvOF}
                    getStatusBadge={getStatusBadge}
                    selectionMode={selectionMode}
                    isSelected={selectedOfsForExport.has(of.id)}
                    onToggleSelect={toggleOfSelection}
                  />
                ))}
              </tbody>
            </table>
          </Card>
        )}

        {/* Controlos de Paginação */}
        {filteredOfs.length > 0 && (
          <div className="flex items-center justify-between mt-4 bg-white rounded-xl p-4 border border-stone-200">
            <div className="flex items-center gap-4">
              <span className="text-sm text-stone-600">
                A mostrar <strong>{startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredOfs.length)}</strong> de <strong>{filteredOfs.length}</strong> OFs
              </span>
              <div className="flex items-center gap-2">
                <span className="text-sm text-stone-500">Por página:</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="border border-stone-300 rounded-lg px-2 py-1 text-sm bg-white"
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="px-3"
              >
                ««
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-3"
              >
                «
              </Button>
              
              <span className="px-4 py-1 text-sm font-medium">
                Página {currentPage} de {totalPages || 1}
              </span>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage >= totalPages}
                className="px-3"
              >
                »
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage >= totalPages}
                className="px-3"
              >
                »»
              </Button>
            </div>
          </div>
        )}

        {filteredOfs.length === 0 && <OFEmptyState />}
      </div>



      {/* Dialog Transitar Status */}
      {/* Dialog Status */}
      <OFStatusDialog
        open={showStatusDialog}
        onOpenChange={(open) => { setShowStatusDialog(open); if (!open) { setSelectedOF(null); setStatusObservacao(''); } }}
        selectedOF={selectedOF}
        statusObservacao={statusObservacao}
        setStatusObservacao={setStatusObservacao}
        onTransitarStatus={handleTransitarStatus}
        loading={loading}
        getStatusBadge={getStatusBadge}
      />

      {/* Dialog Progresso Detalhado */}
      <OFProgressDialog
        open={showProgressDialog}
        onOpenChange={(open) => { 
          setShowProgressDialog(open); 
          if (!open) { 
            setProgressoDetalhado(null); 
            setParticipantes([]); 
          } 
        }}
        progressoDetalhado={progressoDetalhado}
        participantes={participantes}
        onEditRegistos={(op) => {
          setSelectedOpForRegistos(op);
          setShowRegistosDialog(true);
        }}
      />

      {/* Acompanhamento Fullscreen */}
      {showAcompanharFullscreen && selectedOfForAcompanhar && (
        <AcompanhamentoFullscreen
          ofId={selectedOfForAcompanhar.id}
          numeroOf={selectedOfForAcompanhar.numero_of}
          marca={selectedOfForAcompanhar.cliente}
          dataEntrega={selectedOfForAcompanhar.data_entrega}
          loteIdPreSelecionado={selectedOfForAcompanhar.loteIdPreSelecionado}
          onClose={() => {
            setShowAcompanharFullscreen(false);
            setSelectedOfForAcompanhar(null);
          }}
        />
      )}

      {/* Modal de Análise de Tempos */}
      <AnaliseTemposOF
        open={showAnaliseTempos}
        onOpenChange={(open) => {
          setShowAnaliseTempos(open);
          if (!open) setSelectedOfForAnalise(null);
        }}
        of={selectedOfForAnalise}
        operacoesBase={operacoes}
        categorias={allCategorias}
        quantidade={selectedOfForAnalise?.quantidade}
      />

            {/* Modal Resgatar Peças para Arranjo */}
      {/* Modal Criar Resgate */}
      <ModalCriarResgate
        open={showResgateModal}
        onOpenChange={setShowResgateModal}
        anomaliasProducao={anomaliasProducao}
        anomaliasSelecionadas={anomaliasSelecionadas}
        setAnomaliasSelecionadas={setAnomaliasSelecionadas}
        getQuantidadeDisponivelResgate={getQuantidadeDisponivelResgate}
        onCriar={handleCriarResgate}
        loading={loading}
      />

      {/* Dialog Gestão de Lotes */}
      {showLotesDialog && selectedOfForLotes && (
        <GestaoLotes
          ofId={selectedOfForLotes.id}
          numeroOf={selectedOfForLotes.numero_of}
          quantidadeTotal={selectedOfForLotes.quantidade_total}
          marca={selectedOfForLotes.cliente}
          dataEntrega={selectedOfForLotes.data_entrega}
          categoriasEspeciais={selectedOfForLotes.categorias_especiais || []}
          onClose={() => {
            setShowLotesDialog(false);
            setSelectedOfForLotes(null);
          }}
          onUpdate={loadOfs}
          onEditOperacoes={() => {
            // Fechar gestão de lotes e abrir editor de OF diretamente na tab de operações
            const ofToEdit = selectedOfForLotes;
            setShowLotesDialog(false);
            setSelectedOfForLotes(null);
            if (ofToEdit) {
              openEditDialogOperacoes(ofToEdit);
            }
          }}
        />
      )}


      {/* Modal Registos de Produção */}
      <ModalRegistosProducao
        open={showRegistosDialog}
        onOpenChange={(v) => {
          setShowRegistosDialog(v);
          if (!v) { setSelectedOpForRegistos(null); setRegistosOperacao([]); }
        }}
        operacao={selectedOpForRegistos}
        registos={registosOperacao}
        onEditRegisto={(id) => { setSelectedRegistoId(id); setShowEditarRegisto(true); }}
      />

      {/* Modal de Edição de Registo Individual */}
      <EditarRegisto
        open={showEditarRegisto}
        onOpenChange={setShowEditarRegisto}
        registoId={selectedRegistoId}
        onSuccess={() => {
          // Recarregar registos e progresso
          if (progressoDetalhado?.of?.id && selectedOpForRegistos) {
            loadRegistosOperacao(progressoDetalhado.of.id, selectedOpForRegistos.operacao_id);
            loadProgressoDetalhado(progressoDetalhado.of.id);
          }
        }}
      />


      {/* Modal Nova OF */}
      <ModalNovaOF
        open={showDialog}
        onOpenChange={setShowDialog}
        operacoes={operacoes}
        allCategorias={allCategorias}
        categoriasEspeciais={categoriasEspeciais}
        operadores={operadores}
        modelos={modelos}
        clientesDB={clientesDB}
        allTags={allTags}
        loading={loading}
        setLoading={setLoading}
        onSuccess={() => { loadOfs(); loadOfsPorEstado(); }}
        filtrarOperadoresPorPermissao={filtrarOperadoresPorPermissao}
        existeOperadorComPermissao={existeOperadorComPermissao}
      />

      {/* Modal Editar OF */}
      <ModalEditarOF
        of={editOf}
        open={showEditDialog}
        onOpenChange={(v) => { setShowEditDialog(v); if (!v) resetForm(); }}
        initialStep={editInitialStep}
        operacoes={operacoes}
        allCategorias={allCategorias}
        categoriasEspeciais={categoriasEspeciais}
        operadores={operadores}
        modelos={modelos}
        clientesDB={clientesDB}
        allTags={allTags}
        loading={loading}
        setLoading={setLoading}
        isAdmin={isAdmin}
        onSuccess={() => { loadOfs(); loadOfsPorEstado(); }}
        filtrarOperadoresPorPermissao={filtrarOperadoresPorPermissao}
        existeOperadorComPermissao={existeOperadorComPermissao}
        onCriarMinuta={handleCriarMinutaCallback}
        onOpenResgate={handleOpenResgateFromEdit}
        resgates={resgates}
      />

      {/* Modal Criar Minuta */}
      <ModalCriarMinuta
        open={showCriarMinutaModal}
        onOpenChange={setShowCriarMinutaModal}
        initialData={minutaInitialData}
        operacoes={operacoes}
        categorias={allCategorias}
        allCategorias={allCategorias}
        operadores={operadores}
        loading={loading}
        setLoading={setLoading}
        onSave={async (payload) => {
          await axios.post(`${API}/modelos`, payload);
          toast.success('Minuta criada com sucesso!');
          loadModelos();
        }}
        filtrarOperadoresPorPermissao={filtrarOperadoresPorPermissao}
        existeOperadorComPermissao={existeOperadorComPermissao}
      />

      {/* Dialog de confirmação de eliminação */}
      <AlertDialog open={!!deleteConfirmOF} onOpenChange={(open) => { if (!open) setDeleteConfirmOF(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar OF "{deleteConfirmOF?.numero_of}"?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser revertida e irá apagar todos os dados associados (lotes, registos, anomalias).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteOF} className="bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </GestorLayout>
  );
}

// Componente wrapper com OFProvider
export default function GestaoOFsWithProvider() {
  return (
    <OFProvider>
      <GestaoOFsContent />
    </OFProvider>
  );
}
